/**
 * Phone numbers: add, edit, set primary, choose channels, remove.
 *
 * The primary is a radio, not a checkbox, because exactly one number is primary
 * — a rule the server enforces in a single transaction. Rendering it as a
 * checkbox would suggest a state ("two primaries", "none") that cannot exist.
 *
 * Removal is the one hard delete in MeshHub and is guarded server-side, so the
 * confirm dialog says «این حذف قطعی است» rather than the softer wording every
 * deactivation uses. A refusal from the domain guard surfaces as the error
 * message it came with, not a generic failure.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Card } from '@meshhub/design-tokens/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { ChannelIcons } from '../ChannelIcons.jsx';
import { formatPhone, fromPersianDigits } from '../../lib/format.js';
import {
  ADD_PHONE,
  ADMIN_LOOKUPS_QUERY,
  REMOVE_PHONE,
  SET_PHONE_CHANNELS,
  SET_PRIMARY_PHONE,
  UPDATE_PHONE,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import {
  CheckboxField,
  ConfirmDialog,
  FormDialog,
  MutationError,
  TextField,
} from '../FormKit.web.jsx';

export function PhonesPanel({ contact, onSaved, readOnly }) {
  const [editing, setEditing] = useState(null); // null | 'new' | phone object
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState(null);

  const { data: channelData } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'COMMUNICATION_CHANNEL', includeInactive: false },
  });
  const channels = channelData?.lookups ?? [];

  const [setPrimary] = useMutation(SET_PRIMARY_PHONE);
  const [setChannels] = useMutation(SET_PHONE_CHANNELS);
  const [removePhone] = useMutation(REMOVE_PHONE);

  const run = async (fn) => {
    setError(null);
    try {
      await fn();
      await onSaved?.();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-md font-bold">{S.contacts.detail.phones}</h2>
        {!readOnly && (
          <Button variant="ghost" className="ms-auto" onClick={() => setEditing('new')}>
            <Plus size={14} aria-hidden="true" />
            {S.contacts.detail.addPhone}
          </Button>
        )}
      </div>

      <MutationError error={error} />

      {contact.phoneNumbers.length === 0 ? (
        <p className="text-text-muted">{S.contacts.detail.noPhones}</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {contact.phoneNumbers.map((phone) => (
            <li key={phone.id} className="flex flex-col gap-2 border-b border-border pb-4 last:border-b-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`primary-${contact.id}`}
                    className="accent-primary"
                    checked={phone.isPrimary}
                    disabled={readOnly}
                    onChange={() =>
                      run(() => setPrimary({ variables: { phoneNumberId: phone.id } }))
                    }
                    aria-label={S.contacts.detail.makePrimary}
                  />
                  <span dir="ltr" className="font-medium tabular-nums">
                    {formatPhone(phone.number)}
                  </span>
                </label>

                {phone.label && <span className="text-sm text-text-muted">{phone.label}</span>}
                {phone.isPrimary && (
                  <span className="text-xs text-primary">{S.contacts.detail.primary}</span>
                )}

                <span className="ms-auto flex items-center gap-2">
                  <ChannelIcons channels={phone.channels} />
                  {!readOnly && (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => setEditing(phone)}
                        aria-label={S.contacts.detail.editPhone}
                        title={S.contacts.detail.editPhone}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setRemoving(phone)}
                        aria-label={S.app.remove}
                        title={S.app.remove}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </Button>
                    </>
                  )}
                </span>
              </div>

              {!readOnly && (
                <div className="flex flex-wrap gap-4 ps-6">
                  {channels.map((channel) => {
                    const active = phone.channels.some((c) => c.code === channel.code);
                    return (
                      <CheckboxField
                        key={channel.id}
                        label={channel.labelFa}
                        checked={active}
                        onChange={() =>
                          run(() =>
                            setChannels({
                              variables: {
                                phoneNumberId: phone.id,
                                channelCodes: active
                                  ? phone.channels
                                      .filter((c) => c.code !== channel.code)
                                      .map((c) => c.code)
                                  : [...phone.channels.map((c) => c.code), channel.code],
                              },
                            }),
                          )
                        }
                      />
                    );
                  })}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <PhoneDialog
          contactId={contact.id}
          phone={editing === 'new' ? null : editing}
          channels={channels}
          open={Boolean(editing)}
          onOpenChange={(next) => !next && setEditing(null)}
          onSaved={onSaved}
        />
      )}

      <ConfirmDialog
        open={Boolean(removing)}
        onOpenChange={(next) => !next && setRemoving(null)}
        title={S.app.remove}
        message={removing ? S.contacts.detail.confirmRemovePhone(formatPhone(removing.number)) : ''}
        confirmLabel={S.app.remove}
        danger
        onConfirm={() =>
          run(() => removePhone({ variables: { phoneNumberId: removing.id } }))
        }
      />
    </Card>
  );
}

function PhoneDialog({ contactId, phone, channels, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(() => ({
    numberRaw: phone?.number ?? '',
    label: phone?.label ?? '',
    notes: phone?.notes ?? '',
    channelCodes: phone?.channels.map((c) => c.code) ?? [],
  }));
  const [error, setError] = useState(null);

  const [addPhone, { loading: adding }] = useMutation(ADD_PHONE);
  const [updatePhone, { loading: updating }] = useMutation(UPDATE_PHONE);
  const [setChannels] = useMutation(SET_PHONE_CHANNELS);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const toggle = (code) =>
    set({
      channelCodes: form.channelCodes.includes(code)
        ? form.channelCodes.filter((c) => c !== code)
        : [...form.channelCodes, code],
    });

  async function submit() {
    setError(null);
    // Digits are normalized here so the API always receives ASCII, whichever
    // keyboard the user had open (CLAUDE.md §12).
    const numberRaw = fromPersianDigits(form.numberRaw).trim();
    try {
      if (phone) {
        await updatePhone({
          variables: {
            phoneNumberId: phone.id,
            numberRaw,
            label: form.label.trim(),
            notes: form.notes.trim(),
          },
        });
        await setChannels({
          variables: { phoneNumberId: phone.id, channelCodes: form.channelCodes },
        });
      } else {
        await addPhone({
          variables: {
            contactId,
            phoneNumber: {
              numberRaw,
              label: form.label.trim() || null,
              notes: form.notes.trim() || null,
              channelCodes: form.channelCodes,
            },
          },
        });
      }
      await onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={phone ? S.contacts.detail.editPhone : S.contacts.detail.addPhone}
      onSubmit={submit}
      busy={adding || updating}
    >
      <MutationError error={error} />

      <TextField
        label={S.contacts.detail.phoneNumber}
        value={form.numberRaw}
        onChange={(e) => set({ numberRaw: e.target.value })}
        dir="ltr"
        inputMode="tel"
        required
      />
      <TextField
        label={S.contacts.detail.phoneLabel}
        value={form.label}
        onChange={(e) => set({ label: e.target.value })}
      />
      <TextField
        label={S.contacts.detail.phoneNotes}
        value={form.notes}
        onChange={(e) => set({ notes: e.target.value })}
      />

      <fieldset className="flex flex-wrap gap-4">
        <legend className="mb-2 text-sm text-text-muted">{S.contacts.detail.channels}</legend>
        {channels.map((channel) => (
          <CheckboxField
            key={channel.id}
            label={channel.labelFa}
            checked={form.channelCodes.includes(channel.code)}
            onChange={() => toggle(channel.code)}
          />
        ))}
      </fieldset>
    </FormDialog>
  );
}
